import { type UIViewInjectedProps, UIView } from '@uirouter/react';
import { Descriptions, Tabs } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { Drawer } from '@/components/drawer';
import { farmerStore, farmerPlotsStore } from '@/stores';
import { getFormattedYear } from '@/utils/moment';
import { FarmerSourceDocumentType } from '@/types/enums';

import { FarmerFarmsTable } from './farms-table';
import { Plots } from './plots';

const MAX_WIDTH = 1300;
const { TabPane } = Tabs;

const SOURCE_DOCUMENT_TYPE_LABELS: Partial<Record<FarmerSourceDocumentType, string>> = {
  [FarmerSourceDocumentType.RA]: 'Rainforest Alliance',
  [FarmerSourceDocumentType.FT]: 'Fairtrade',
  [FarmerSourceDocumentType.MERIDIA_DOCUMENT]: 'Meridia',
};

export const FarmerView = observer(({ transition }: UIViewInjectedProps) => {
  const farmerId = +transition.router.globals.params?.farmerId;

  useEffect(() => {
    if (farmerId) {
      farmerPlotsStore.load(farmerId);
    }
    return () => {
      farmerPlotsStore.clear();
    };
  }, [farmerId]);

  const farmer = farmerStore.items.find(({ id }: any) => id === farmerId);

  useEffect(() => {
    if (farmerId && !farmer) {
      api.farmers
        .getOne(farmerId)
        .source.then(({ data }) => farmerStore.addItem(data))
        .catch(() => {});
    }
  }, [farmerId, farmer]);

  const title = `Farmer ${(farmer?.farmerId || farmer?.farms?.[0]?.farmId || farmer?.id) ?? ''}`;

  const onClose = () => {
    transition.router.stateService.go('base-layout.farmers');
  };

  const onOpenFarm = (farmId: number) => {
    transition.router.stateService.go('base-layout.farms.view', { farmId });
  };

  const width = window.innerWidth - 250 > MAX_WIDTH ? MAX_WIDTH : window.innerWidth - 250;
  const item = farmer ?? ({} as cyrian.api.farmers.Farmer);

  return (
    <>
      <Drawer onClose={onClose} visible={true} title={title} width={width}>
        <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label={<FormattedMessage id="farmers.farmerId" />}>
            {item.farmerId ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farmers.firstName" />}>
            {item.firstName ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farmers.lastName" />}>
            {item.lastName ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farmers.gender" />}>{item.gender ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farmers.phone" />}>{item.phone ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farmers.dob" />}>
            {item.dob != null ? getFormattedYear(item.dob) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farmers.nationalId" />}>
            {item.nationalId ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farmers.sourceDocumentType" />}>
            {item.sourceDocumentType != null
              ? (SOURCE_DOCUMENT_TYPE_LABELS[item.sourceDocumentType] ?? item.sourceDocumentType)
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farmers.cooperative" />}>
            {item.cooperatives?.length ? item.cooperatives.map((c) => c.name).join(', ') : '—'}
          </Descriptions.Item>
        </Descriptions>
        <Tabs defaultActiveKey="farms">
          <TabPane tab={<FormattedMessage id="farms.tabTitle" />} key="farms">
            <FarmerFarmsTable farmerId={farmerId} onOpenFarm={onOpenFarm} />
          </TabPane>
          <TabPane tab={<FormattedMessage id="farmers.plotsTab" />} key="plots">
            <Plots farmerId={farmerId} farmer={farmer} />
          </TabPane>
        </Tabs>
      </Drawer>
      <UIView />
    </>
  );
});
